import React, { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";

import {
  createReminder,
  getReminderPreferencesByPracticeNumbers,
  updateReminder,
} from "../../acommsapi";

/**
 * WhatsAppReminderMapping — maps an approved template to each reminder
 * interval, and each of its {{n}} placeholders to an appointment field.
 *
 * Why a mapping rather than a text box: WhatsApp cannot send interpolated free
 * text outside the 24-hour service window. A reminder is a template name plus
 * an ordered parameter list, so the practice picks the template and says what
 * goes in each slot.
 *
 * Visual conventions: /CLAUDE.md §1.7 (labels above inputs, isInvalid
 * feedback), §1.4 (buttons).
 */

// The ${...} expressions fillTemplate (commsRoutes.js:281) already understands,
// with labels a receptionist can choose from. These are literal strings that
// the BACKEND interpolates, not template literals — hence the disable.
/* eslint-disable no-template-curly-in-string */
const PARAM_OPTIONS = [
  { value: "${appointment.patientFirstName}", label: "Patient first name" },
  { value: "${appointment.patientName}", label: "Patient full name" },
  { value: "${practice.practiceName}", label: "Practice name" },
  { value: "${appointment.date}", label: "Appointment date" },
  { value: "${appointment.time}", label: "Appointment time" },
  { value: "${appointment.location.name}", label: "Location name" },
  { value: "${appointment.practiceName}", label: "Practice name (appointment)" },
];

const SAMPLE = {
  "${appointment.patientFirstName}": "Thandi",
  "${appointment.patientName}": "Thandi Mokoena",
  "${practice.practiceName}": "Green Valley Physio",
  "${appointment.practiceName}": "Green Valley Physio",
  "${appointment.date}": "12 March 2026",
  "${appointment.time}": "10:30",
  "${appointment.location.name}": "Main Rooms",
};

/* eslint-enable no-template-curly-in-string */

function renderPreview(body, paramPaths) {
  if (!body) return "";
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
    const path = paramPaths[Number(n) - 1];
    return SAMPLE[path] || "…";
  });
}

function IntervalMapping({ title, subtitle, approved, value, onChange }) {
  const selected = approved.find(
    (t) => t.name === value?.name && t.language === value?.language,
  );
  const paramCount = selected?.paramCount || 0;
  const paramPaths = value?.paramPaths || [];
  const incomplete =
    !!selected && paramPaths.filter(Boolean).length !== paramCount;

  return (
    <div className="mb-3">
      <h6 className="mb-0 text-primary fw-bold">{title}</h6>
      <p className="mb-2">
        <small className="text-muted">{subtitle}</small>
      </p>

      <Form.Group className="mb-2">
        <Form.Label>Template</Form.Label>
        <Form.Select
          value={selected ? `${selected.name}::${selected.language}` : ""}
          onChange={(e) => {
            if (!e.target.value) return onChange(null);
            const [name, language] = e.target.value.split("::");
            onChange({ name, language, paramPaths: [] });
          }}
        >
          <option value="">Not sending WhatsApp reminders</option>
          {approved.map((t) => (
            <option key={`${t.name}::${t.language}`} value={`${t.name}::${t.language}`}>
              {t.name} ({t.language})
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {selected && (
        <>
          <Row className="g-2 mb-2">
            {Array.from({ length: paramCount }).map((_, index) => (
              <Col xs={12} md={6} key={index}>
                <Form.Group>
                  <Form.Label>{`Value for {{${index + 1}}}`}</Form.Label>
                  <Form.Select
                    value={paramPaths[index] || ""}
                    isInvalid={!paramPaths[index]}
                    onChange={(e) => {
                      const next = [...paramPaths];
                      next[index] = e.target.value;
                      onChange({ ...value, paramPaths: next });
                    }}
                  >
                    <option value="">Choose a field…</option>
                    {PARAM_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Every value must be mapped.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            ))}
          </Row>

          <div className="posted-invoice-info">
            <span className="acc-card-label">Preview</span>
            <div>{renderPreview(selected.body, paramPaths)}</div>
          </div>

          {incomplete && (
            <Alert variant="warning" className="py-2 px-3 mt-2 mb-0">
              Map every value before saving — a reminder with a blank value is
              rejected by WhatsApp.
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

export default function WhatsAppReminderMapping({
  practiceNumber,
  templates,
  canConfigure,
  showToast,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [oneDay, setOneDay] = useState(null);
  const [sunday, setSunday] = useState(null);

  const approved = useMemo(
    () => templates.filter((t) => t.status === "APPROVED"),
    [templates],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const result = await getReminderPreferencesByPracticeNumbers(
          [practiceNumber],
          "WA",
        );
        if (cancelled) return;

        // The endpoint wraps its result as { success, count, data: [...] } —
        // the same shape SMSComms unwraps. Reading the wrapper as the list
        // silently loaded nothing AND left docId null, so every save created a
        // new document instead of updating the existing one.
        const docs = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
          ? result.data
          : [];
        const doc = docs[0];

        if (doc) {
          setDocId(doc._id);
          setOneDay(doc["1DayTemplate"] || null);
          setSunday(doc.sundayTemplate || null);
        }
      } catch (_err) {
        // No mapping yet is the normal first-run state, not an error.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [practiceNumber]);

  const complete = (mapping) => {
    if (!mapping) return true;
    const template = approved.find(
      (t) => t.name === mapping.name && t.language === mapping.language,
    );
    if (!template) return false;
    return (
      (mapping.paramPaths || []).filter(Boolean).length === template.paramCount
    );
  };

  const canSave = complete(oneDay) && complete(sunday);

  const save = async () => {
    setSaving(true);
    try {
      const previewOf = (mapping) => {
        if (!mapping) return "";
        const template = approved.find(
          (t) => t.name === mapping.name && t.language === mapping.language,
        );
        return template ? renderPreview(template.body, mapping.paramPaths) : "";
      };

      const payload = {
        reminderType: "WA",
        "1DayWAPracs": oneDay ? [practiceNumber] : [],
        sundayWAPracs: sunday ? [practiceNumber] : [],
        "1DayWAText": previewOf(oneDay),
        sundayWAText: previewOf(sunday),
        "1DayTemplate": oneDay,
        sundayTemplate: sunday,
      };

      if (docId) {
        await updateReminder(docId, payload);
      } else {
        const created = await createReminder(payload);
        const newId = created?.data?._id || created?._id || null;
        if (!newId) {
          // Without an id the next save would create ANOTHER document. Reload
          // rather than leave the component in that state.
          console.warn("Reminder created but no id returned; reloading.");
        }
        setDocId(newId);
      }
      showToast?.("success", "WhatsApp reminder settings saved.");
    } catch (err) {
      showToast?.(
        "danger",
        err?.response?.data?.error || "Could not save the reminder settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="acc-list mb-3 text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading reminder settings…</p>
      </div>
    );
  }

  if (!approved.length) {
    return (
      <div className="acc-list mb-3">
        <h6 className="mb-2 text-primary fw-bold">Appointment reminders</h6>
        <p className="mb-0">
          No approved templates yet. Provision the standard reminders above and
          wait for Meta's approval before mapping them.
        </p>
      </div>
    );
  }

  return (
    <div className="acc-list mb-3">
      <h6 className="mb-3 text-primary fw-bold">Appointment reminders</h6>

      <IntervalMapping
        title="One day before"
        subtitle="Sent every afternoon for the next day's appointments."
        approved={approved}
        value={oneDay}
        onChange={setOneDay}
      />

      <IntervalMapping
        title="Sunday for the week"
        subtitle="Sent on Sunday morning for the week ahead."
        approved={approved}
        value={sunday}
        onChange={setSunday}
      />

      {canConfigure && (
        <Button
          variant="primary"
          onClick={save}
          disabled={saving || !canSave}
          style={{ width: "120px" }}
        >
          {saving ? <Spinner size="sm" animation="border" /> : "Save"}
        </Button>
      )}
    </div>
  );
}
